/**
 * Where a save's packing runs: in a worker when the browser gives one, inline
 * when it does not. One implementation, two hosts.
 *
 * `packText` held the page for about 0.8 s per save on the 12 MB evidence
 * fixture (D182 left it there, because moving it touches the durable boundary).
 * It is a pure function of the save's JSON, which the queue builds once, so it is
 * posted to `packWorker.ts` and the page carries on while it deflates. What the
 * boundary promises does not move, because none of it is here: the queue still
 * awaits this before it writes, the codec still reads the JSON back before the
 * transaction, the transaction still commits and is read back exactly before
 * anything says saved (`archive.ts`, `docStore.ts`). This only decides which
 * thread computes a string both threads would compute identically.
 *
 * WHEN THERE IS NO WORKER — node and vitest, a browser without one, a constructor
 * that throws, a script that will not load (an old deploy's hashed name, a network
 * gone at the first save), a worker that dies — the same `packText` runs here, on
 * the page, as it did before this file existed. A failure is never a failed save,
 * only a slower one. The first failure retires the worker for the life of the page
 * and every request still waiting is answered inline, so nothing is left pending
 * on a thread that has gone.
 *
 * Same-origin and bundled — never a `blob:` URL. `public/_headers` sets no
 * `worker-src`, so workers fall under `script-src 'self'`, which admits a script
 * this origin serves and refuses a blob.
 */
import { packText } from './pack'
import type { PackReply, PackRequest } from './packWorker'

let worker: Worker | null = null
/** Set on the first failure; from then on every pack runs inline. */
let retired = false
/** Where the latest pack ran — for `packingHost`. */
let ranOn: 'worker' | 'inline' | null = null
let lastId = 0
const waiting = new Map<number, { plain: string; resolve: (packed: string) => void }>()

function retire(): void {
  retired = true
  worker?.terminate()
  worker = null
  // Held until now for exactly this: the text is still here to pack inline, so a
  // worker lost mid-save costs time and never the save.
  const jobs = [...waiting.values()]
  waiting.clear()
  for (const job of jobs) job.resolve(inline(job.plain))
}

function inline(plain: string): string {
  ranOn = 'inline'
  return packText(plain)
}

function started(): Worker | null {
  if (worker || retired) return worker
  if (typeof Worker !== 'function') return null
  try {
    // The literal form Vite recognises and emits as its own chunk: same origin,
    // hashed with the build, loaded the first time anything is saved.
    worker = new Worker(new URL('./packWorker.ts', import.meta.url), {
      type: 'module',
      name: 'pack',
    })
  } catch {
    retired = true
    return null
  }
  worker.onmessage = (event: MessageEvent<PackReply>) => {
    const job = waiting.get(event.data.id)
    if (!job) return
    waiting.delete(event.data.id)
    ranOn = 'worker'
    job.resolve(event.data.packed)
  }
  worker.onerror = (event) => {
    // Handled here; the console needs no second report of a fallback that works.
    event.preventDefault()
    retire()
  }
  worker.onmessageerror = retire
  return worker
}

/**
 * `packText(plain)`, computed off the main thread when a worker is available.
 * Never rejects, and resolves to exactly what `packText(plain)` returns — the
 * worker runs that function, and every fallback calls it here.
 */
export function packTextOffThread(plain: string): Promise<string> {
  const host = started()
  if (!host) return Promise.resolve(inline(plain))
  const id = ++lastId
  return new Promise((resolve) => {
    waiting.set(id, { plain, resolve })
    try {
      host.postMessage({ id, plain } satisfies PackRequest)
    } catch {
      retire()
    }
  })
}

/** Where the latest pack ran, or null before anything was packed. For the
 *  browser suite and for measuring; nothing decides anything on it. */
export const packingHost = (): 'worker' | 'inline' | null => ranOn
